import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { userAppDataContext, fetchHomeKVStore, selectHomeKVStore } from '@moderepo/mode-data-state-user-app';
import { fetchAgentProjectById, ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { Agent } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { Dashboard, HOME_DASHBOARDS_KV_KEY, isHomeDashboardsKeyValuePair, isProjectDashboardsSettings } from './dashboardTypes';
import { DashboardLogicApiCallResponse, deleteHomeDashboard, deleteProjectDashboard } from './dashboardLogic';
import { DashboardSettingsContainer } from './DashboardSettingsContainer';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface DashboardPreviewContainerProps {
    readonly projectId: number;

    // dashboardId is undefined means the user is in CREATE dashboard mode.
    readonly dashboardId?: number | undefined;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly homeId?: number | undefined;
    }
}


/**
 * This Component is used for displaying a DashboardWidget in the preview panel for preview
 */
export const DashboardPreviewContainer: React.FC<DashboardPreviewContainerProps> = (props: DashboardPreviewContainerProps) => {
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    const isNewDashboard = !props.dashboardId;

    const selectedDashboard = useMemo(() => {
        // If dashboardId is provided, that mean this Preview panel is being used to display a dashboard settings. We need to fetch the
        // data for the provided dashboardId.
        if (props.dashboardId) {
            if (props.routeContext?.homeId) {
                // Get the list dashboards from the Home KV and then try to find the dashboard with ID equal props.dashboardId
                const dashboardKV = selectHomeKVStore(appDataState, props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY);
                if (isHomeDashboardsKeyValuePair(dashboardKV)) {
                    const dashboards = dashboardKV.value;
                    return dashboards.find((db) => {
                        return db.id === props.dashboardId;
                    });
                }
            } else if (project && isProjectDashboardsSettings(project?.supportToolSettings.dashboardSettings)) {
                const { dashboards } = project.supportToolSettings.dashboardSettings;
                return dashboards.find((dashboard) => {
                    return dashboard.id === props.dashboardId;
                });
            }
        }

        return undefined;
    }, [appDataState, project, props.dashboardId, props.routeContext]);



    /**
     * This useEffect is used for fetching the Home KV store which is used for storing the provided homeId's dashboard. We need to load
     * the home Dashboard in order to get the widget settings for the selected widget
     */
    useEffect(() => {
        // This container only need the dashboard data so that it can display the dashboard name when confirm if the user want to
        // delete the dashboard. Therefore this component doesn't need the latest dashboard data. If we already have data from cache
        // then we can use that data
        if (selectedDashboard) {
            return;
        }

        if (props.routeContext?.homeId) {
            appDataDispatch(fetchHomeKVStore(props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY), true, true).catch(() => {
                // ignore error because appDataDispatch already handled the error
            });
        } else {
            dataDispatch(fetchAgentProjectById(loggedInAgent.id, props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appDataDispatch, dataDispatch, loggedInAgent.id, props.projectId, props.routeContext, selectedDashboard]);


    /**
     * Unselect the selected widget by closing the slide-out panel
     */
    const hidePreview = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    /**
     * On create new dashboard canceled event from Dashboard we need to hide the slide-out panel
     */
    const onNewDashboardCanceled = useCallback(() => {
        hidePreview();
    }, [hidePreview]);



    /**
     * On create new dashboard success, we will close the slide-out panel
     */
    const onNewDashboardCreated = useCallback(() => {
        hidePreview();
    }, [hidePreview]);



    /**
     * use useMemo to create the the newDashboardHandler so that this doesn't get created on every re-render. All this function does is return an
     * object with 2 callback functions.newDashboardHandler
     */
    const newWidgetHandler = useMemo(() => {
        return {
            onNewDashboardCreated,
            onNewDashboardCanceled,
        };
    }, [onNewDashboardCanceled, onNewDashboardCreated]);


    /**
     * On Delete dashboard button clicked. We will confirm before removing the dashboard from the dashboard.
     */
    const onDeleteDashboardActionClicked = useCallback((dashboardToRemove: Dashboard, errorMessage?: string | undefined) => {
        // Confirm before removing dashboard
        uiDispatch(showDialog({
            type          : DialogType.INPUT,
            title         : 'dialog.generic_confirm_delete.title',
            titleData     : { object_term: capitalizeAll(trans('dashboard.label')) },
            message       : 'dialog.generic_confirm_delete.message',
            messageData   : { object_name: dashboardToRemove.title, object_term: 'dashboard.label', object_field: 'label.name' },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : capitalizeAll(trans('label.name')),
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'dashboard.label',
                    object_field: 'label.name',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const userResponse = response.input ? response.input.trim() : undefined;

                    if (!userResponse) {
                        // User input is not provided
                        onDeleteDashboardActionClicked(dashboardToRemove, 'error.input.generic.empty');
                    } else if (userResponse !== dashboardToRemove.title) {
                        // user input does not match dashboardTitile
                        onDeleteDashboardActionClicked(dashboardToRemove, 'error.input.generic.invalid');

                    } else {
                        const onCompleted = (result: DashboardLogicApiCallResponse) => {
                            if (result.success) {
                                uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', { term: trans('dashboard.label') }));

                                // The dashboard is deleted so we should hide the preview panel
                                hidePreview();
                            } else if (result.errorMessageKey) {
                                uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
                            }
                        };

                        if (props.routeContext?.homeId) {
                            deleteHomeDashboard(appDataState, appDataDispatch, props.routeContext.homeId, dashboardToRemove.id).then(onCompleted);
                        } else {
                            deleteProjectDashboard(dataState, dataDispatch, props.projectId, loggedInAgent.id, dashboardToRemove.id)
                                .then(onCompleted);
                        }
                    }
                }
            },
        }));
    }, [uiDispatch, props.routeContext, props.projectId, appDataState, appDataDispatch, trans, hidePreview, dataState, dataDispatch,
        loggedInAgent.id]);


    /**
     * Create an Array of primary actions for the dashboard widget
     */
    const primaryActions = useMemo(() => {
        return [];
    }, []);


    /**
     * Create secondary actions for the dashboard
     */
    const secondaryActions = useMemo(() => {
        if (selectedDashboard) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans('dashboard.action.delete')),
                onClick  : () => {
                    onDeleteDashboardActionClicked(selectedDashboard);
                },
            }];
            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, onDeleteDashboardActionClicked, selectedDashboard, trans]);



    /**
     * Create the content for the slide-out panel. The content will just going to be a Dashboard
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <DashboardSettingsContainer
                className="transparent"
                projectId={projectId}
                dashboardId={props.dashboardId}
                newDashboardHandlers={
                    isNewDashboard ? newWidgetHandler : undefined
                }
                routeContext={props.routeContext}
            />
        );
    }, [isNewDashboard, newWidgetHandler, projectId, props.dashboardId, props.routeContext]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewDashboard}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
